// src/services/problemService.ts

import { db } from './firebaseConfig';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  updateDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { Problem } from '../models/Problem';
import { ProblemStatus } from '../models/Problem';

// Firestore function to add a problem
export const addProblemToFirestore = async (userId: string, problem: Omit<Problem, 'id'>) => {
  try {
    // Create a new document in the user's problem collection with an auto-generated ID
    const problemRef = await addDoc(collection(db, 'problems'), problem);

    console.log('Problem added to Firestore successfully with ID:', problemRef.id);

    // Return the Firestore-generated ID
    return problemRef.id;
  } catch (error) {
    console.error('Error adding problem to Firestore:', error);
    throw error;
  }
};
// Firestore function to delete a problem and all related data
export const deleteProblemFromFirestore = async (userId: string, problemId: string) => {
  try {
    // Correct reference to the problem document
    const problemDocRef = doc(db, 'problems', problemId);

    // Delete related subtasks
    const subtasksQuery = query(
      collection(db, 'subtasks'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );

    const subtasksDocs = await getDocs(subtasksQuery);
    const subtaskDeletePromises = subtasksDocs.docs.map(doc => deleteDoc(doc.ref));

    // Delete related roles
    const rollenQuery = query(
      collection(db, 'rollen'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );

    const rollenDocs = await getDocs(rollenQuery);
    const rollenDeletePromises = rollenDocs.docs.map(doc => deleteDoc(doc.ref));

    // Delete related processed tasks
    const rollenProcessedQuery = query(
      collection(db, 'processed_subtasks'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );
    const rollenProcessedDocs = await getDocs(rollenProcessedQuery);
    const rollenProcessedDeletePromises = rollenProcessedDocs.docs.map(doc => deleteDoc(doc.ref));

    // Wait for all deletions to complete
    await Promise.all([
      ...subtaskDeletePromises,
      ...rollenDeletePromises,
      ...rollenProcessedDeletePromises,
    ]);

    // Finally delete the problem
    await deleteDoc(problemDocRef);

    console.log('Problem, related subtasks, and roles deleted from Firestore successfully');
  } catch (error) {
    console.error('Error deleting problem from Firestore:', error);
    throw error; // Rethrow error to handle it in the caller function
  }
};

// Firestore function to fetch problems for a specific user
export const getProblemsFromFirestore = async (userId: string): Promise<Problem[]> => {
  try {
    const problemsQuery = query(collection(db, 'problems'));
    const querySnapshot = await getDocs(problemsQuery);

    const problems: Problem[] = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      problems.push({
        id: doc.id,
        title: data.title, // Ensure the title is retrieved
        description: data.description, // Ensure the description is retrieved
        status: data.status as Problem['status'], // Ensure the status is retrieved and typed correctly
        userId: data.userId, // Ensure the userId is retrieved
        createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : null, // Convert Firebase Timestamp to string
        updatedAt: data.updatedAt ? data.updatedAt.toDate().toISOString() : null, // Convert Firebase Timestamp to string
      });
    });
    return problems;
  } catch (error) {
    console.error('Error fetching problems from Firestore:', error);
    throw error;
  }
};

// Firestore function to update problem status
export const updateProblemStatusInFirestore = async (
  problemId: string, // No userId anymore, problem is stored in a flat collection
  status: ProblemStatus
) => {
  try {
    const problemRef = doc(db, 'problems', problemId); // Reference to the problem document

    // Update the document with the new status and updatedAt timestamp
    await updateDoc(problemRef, {
      status: status,
      updatedAt: serverTimestamp(),
    });

    console.log('Problem status updated in Firestore');
  } catch (error) {
    console.error('Error updating problem status in Firestore:', error);
    throw error;
  }
};
